import React, { useState, useMemo } from 'react';
import { Basket, Symbol, PredictionModel } from './types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  Plus, X, ArrowUp, ArrowDown, ArrowUpDown, Trash, 
  Edit, Pencil, Check, ChevronDown, ChevronUp, InfoIcon, AlertTriangle 
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface BasketManagerProps {
  baskets: Basket[];
  onAddBasket: () => void;
  onUpdateBasket: (basketId: string, updates: Partial<Basket>) => void;
  onRemoveBasket: (basketId: string) => void;
  predictionModels?: PredictionModel[];
}

export const BasketManager: React.FC<BasketManagerProps> = ({
  baskets,
  onAddBasket,
  onUpdateBasket,
  onRemoveBasket,
  predictionModels = []
}) => {
  const [symbolInput, setSymbolInput] = useState<string>('');
  const [symbolDirection, setSymbolDirection] = useState<'long' | 'short' | 'auto'>('long');
  const [editingBasketId, setEditingBasketId] = useState<string | null>(null);

  // Calculate total allocation across all baskets
  const totalAllocation = useMemo(() => {
    return baskets.reduce((sum, basket) => {
      const basketRisk = parseFloat(basket.valueAtRisk || '0');
      return isNaN(basketRisk) ? sum : sum + basketRisk;
    }, 0);
  }, [baskets]);

  // Define color based on allocation percentage
  const getAllocationColor = (total: number) => {
    if (total > 100) return 'bg-red-500';
    if (total > 90) return 'bg-amber-500';
    if (total > 75) return 'bg-yellow-400';
    return 'bg-green-500';
  };

  const handleAddSymbolToBasket = (basketId: string) => {
    if (!symbolInput.trim()) return;
    
    const basket = baskets.find(b => b.id === basketId);
    if (!basket) return;
    
    // Check if symbol already exists in this basket
    if (basket.symbols.some(s => s.ticker.toUpperCase() === symbolInput.toUpperCase())) {
      return; // Symbol already exists
    }
    
    const newSymbol: Symbol = {
      ticker: symbolInput.toUpperCase(),
      direction: symbolDirection,
      weight: 1,
    };
    
    onUpdateBasket(basketId, {
      symbols: [...basket.symbols, newSymbol]
    });
    
    setSymbolInput('');
  };

  const handleRemoveSymbolFromBasket = (basketId: string, ticker: string) => {
    const basket = baskets.find(b => b.id === basketId);
    if (!basket) return;
    
    onUpdateBasket(basketId, {
      symbols: basket.symbols.filter(s => s.ticker !== ticker)
    });
  };

  const getDirectionIcon = (direction: string) => {
    if (direction === 'short') {
      return <ArrowDown className="h-4 w-4 text-red-500" />;
    } else if (direction === 'auto') {
      return <ArrowUpDown className="h-4 w-4 text-purple-500" />;
    } else {
      return <ArrowUp className="h-4 w-4 text-green-500" />;
    }
  };

  const renderAiManagementToggle = (basket: Basket) => {
    if (predictionModels.length === 0) return null;
    
    return (
      <div className="flex items-center">
        <span className="text-sm text-muted-foreground mr-2">AI Managed</span>
        <Switch
          checked={basket.isAiManaged || false}
          onCheckedChange={(checked) => {
            onUpdateBasket(basket.id, { isAiManaged: checked });
          }}
        />
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Total Allocation Indicator */}
      <Card className="border border-muted">
        <CardContent className="pt-4 pb-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <h3 className="text-sm font-medium">Total Risk Allocation</h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-4 w-4 ml-1 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Sum of Value at Risk % across all baskets. This represents the total portfolio allocation committed to these baskets. While it doesn't have to equal 100%, it should not exceed 100%.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center">
              <span className={cn(
                "text-sm font-semibold",
                totalAllocation > 100 ? "text-red-600" : 
                totalAllocation > 90 ? "text-amber-600" : 
                "text-gray-700 dark:text-gray-300"
              )}>
                {totalAllocation.toFixed(1)}%
              </span>
              {totalAllocation > 100 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertTriangle className="h-4 w-4 ml-1 text-red-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Total allocation exceeds 100%. Consider reducing the Value at Risk % for some baskets.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          <div className="relative pt-1">
            <Progress value={Math.min(totalAllocation, 100)} className={cn("h-2", getAllocationColor(totalAllocation))} />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Basket Button */}
      <div className="flex justify-end">
        <Button onClick={onAddBasket} variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Basket
        </Button>
      </div>
      
      {baskets.length === 0 ? (
        <Card className="bg-muted/50 border-dashed border-2 border-gray-300">
          <CardContent className="flex flex-col items-center justify-center p-6">
            <div className="rounded-full bg-primary/10 p-3 mb-4">
              <Plus className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-lg font-medium mb-2">No Baskets Created</h3>
            <p className="text-muted-foreground text-sm text-center mb-4">
              Create baskets to group your symbols and apply specific rules to each group.
            </p>
            <Button onClick={onAddBasket} variant="default">
              Create First Basket
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {baskets.map((basket) => (
            <Card key={basket.id} className="overflow-hidden">
              <div className="px-6 py-4 hover:bg-muted/50 flex items-center justify-between">
                <div className="flex-1 flex items-center">
                  <span className="font-medium">{basket.name}</span>
                  <Badge variant="outline" className="ml-2 text-xs">
                    {basket.symbols.length} Symbol{basket.symbols.length !== 1 ? 's' : ''}
                  </Badge>
                  {basket.isAiManaged && predictionModels.length > 0 && (
                    <Badge className="ml-2 bg-indigo-100 text-indigo-800 hover:bg-indigo-200">
                      AI Managed
                    </Badge>
                  )}
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </div>
                  
              <div className="border-t border-gray-200 dark:border-gray-700">
                <div className="p-6 space-y-4">
                  {/* Basket Settings */}
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Basket Name</div>
                        {renderAiManagementToggle(basket)}
                      </div>
                      <Input
                        id={`basket-name-${basket.id}`}
                        value={basket.name}
                        onChange={(e) => onUpdateBasket(basket.id, { name: e.target.value })}
                        className="max-w-full"
                      />
                    </div>
                  </div>
                  
                  {/* Risk Parameters */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div className="space-y-3">
                      <div className="text-sm font-medium">Profit Target (%)</div>
                      <Input
                        id={`profit-target-${basket.id}`}
                        type="number"
                        min="0"
                        step="0.1"
                        value={basket.profitTarget || ''}
                        onChange={(e) => onUpdateBasket(basket.id, { profitTarget: e.target.value })}
                        className="max-w-full"
                      />
                    </div>
                    
                    <div className="space-y-3">
                      <div className="text-sm font-medium">Stop Loss (%)</div>
                      <Input
                        id={`stop-loss-${basket.id}`}
                        type="number"
                        min="0"
                        step="0.1"
                        value={basket.stopLoss || ''}
                        onChange={(e) => onUpdateBasket(basket.id, { stopLoss: e.target.value })}
                        className="max-w-full"
                      />
                    </div>

                    <div className="space-y-3">
                      <div className="text-sm font-medium">Value at Risk (%)</div>
                      <Input
                        id={`value-at-risk-${basket.id}`}
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={basket.valueAtRisk || ''}
                        onChange={(e) => onUpdateBasket(basket.id, { valueAtRisk: e.target.value })}
                        className="max-w-full"
                        placeholder="% of strategy capital"
                      />
                    </div>
                  </div>
                  
                  {/* Symbol Management */}
                  <div className="space-y-3 pt-4">
                    <div className="text-sm font-medium">Symbols in this Basket</div>
                    
                    <div className="flex items-center space-x-2">
                      <Input
                        type="text"
                        placeholder="Enter symbol (e.g., AAPL)"
                        value={symbolInput}
                        onChange={(e) => setSymbolInput(e.target.value)}
                        className="flex-1"
                      />
                      <Select 
                        value={symbolDirection} 
                        onValueChange={(val) => setSymbolDirection(val as 'long' | 'short' | 'auto')}
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue placeholder="Direction" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="long">Long</SelectItem>
                          <SelectItem value="short">Short</SelectItem>
                          <SelectItem value="auto">Auto</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleAddSymbolToBasket(basket.id)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add
                      </Button>
                    </div>
                    
                    <div className="mt-4">
                      {basket.symbols.length === 0 ? (
                        <div className="text-muted-foreground text-sm italic p-4 border border-dashed rounded-md text-center">
                          No symbols in this basket yet. Add symbols above.
                        </div>
                      ) : (
                        <div className="border rounded-md overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Symbol</th>
                                <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Direction</th>
                                <th scope="col" className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                              {basket.symbols.map((symbol, index) => (
                                <tr key={`${symbol.ticker}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium">
                                    {symbol.ticker}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    <div className="flex items-center">
                                      {getDirectionIcon(symbol.direction)}
                                      <span className="ml-1 text-xs">
                                        {symbol.direction.charAt(0).toUpperCase() + symbol.direction.slice(1)}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handleRemoveSymbolFromBasket(basket.id, symbol.ticker)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <X className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="bg-muted/50 py-3 px-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => onRemoveBasket(basket.id)}
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    Delete Basket
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};